import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/Navbar';
import axiosAuth from '../../config/axios';
import { useEsMovil } from '../../components/Layout';
import { IconShield } from '../../components/Icons';
import { ESTADO_CITA, diaLocal, estilos as s } from './comun';
import { CitaModal, PanelCita, TarjetaCita } from './citas';

// Agenda propia de orientación. No es la Agenda Institucional: solo la ve el
// equipo (profesional: la suya; líder: la de todos, con filtro por orientador).

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function lunesDe(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
function masDias(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export default function AgendaOrientacion() {
  const esMovil = useEsMovil();
  const [estado, setEstado] = useState(null);
  const [lunes, setLunes] = useState(() => lunesDe(new Date()));
  const [vista, setVista] = useState(null);           // 'semana' | 'lista'
  const [citas, setCitas] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [profesional, setProfesional] = useState('');
  const [ocultarCanceladas, setOcultarCanceladas] = useState(true);
  const [nueva, setNueva] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const esLider = estado?.nivel === 'lider';
  const vistaActual = vista || (esMovil ? 'lista' : 'semana');

  useEffect(() => {
    axiosAuth.get('/api/bienestar/estado').then(r => setEstado(r.data.data)).catch(() => setEstado({}));
  }, []);
  useEffect(() => {
    if (esLider) axiosAuth.get('/api/bienestar/equipo/miembros').then(r => setMiembros(r.data.data || [])).catch(() => {});
  }, [esLider]);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const params = new URLSearchParams({ desde: diaLocal(lunes), hasta: diaLocal(masDias(lunes, 6)) });
      if (profesional) params.set('profesional_id', profesional);
      const r = await axiosAuth.get(`/api/bienestar/citas?${params}`);
      setCitas(r.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar la agenda');
    } finally {
      setCargando(false);
    }
  }, [lunes, profesional]);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(
    () => citas.filter(c => !ocultarCanceladas || !['cancelada', 'reprogramada'].includes(c.estado)),
    [citas, ocultarCanceladas]
  );
  const dias = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = masDias(lunes, i);
    const clave = diaLocal(d);
    return { d, clave, citas: visibles.filter(c => diaLocal(c.inicio) === clave) };
  }), [lunes, visibles]);

  const hoy = diaLocal(new Date());
  const rango = `${lunes.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })} – ${masDias(lunes, 6).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  const programadas = citas.filter(c => c.estado === 'programada').length;

  return (
    <div style={s.pagina}>
      <Navbar titulo="Agenda de orientación" />
      <div style={es.contenido}>
        <div style={es.barra}>
          <div style={es.navegacion}>
            <button onClick={() => setLunes(masDias(lunes, -7))} style={es.flecha} aria-label="Semana anterior">‹</button>
            <button onClick={() => setLunes(lunesDe(new Date()))} style={s.btnSecundario}>Hoy</button>
            <button onClick={() => setLunes(masDias(lunes, 7))} style={es.flecha} aria-label="Semana siguiente">›</button>
            <span style={es.rango}>{rango}</span>
          </div>
          <button onClick={() => setNueva(true)} style={s.btnPrimario}>+ Nueva cita</button>
        </div>

        <div style={es.filtros}>
          <div style={es.selector} role="tablist">
            {['semana', 'lista'].map(v => (
              <button key={v} role="tab" aria-selected={vistaActual === v} onClick={() => setVista(v)}
                style={{ ...es.opcion, ...(vistaActual === v ? es.opcionActiva : {}) }}>
                {v === 'semana' ? 'Semana' : 'Lista'}
              </button>
            ))}
          </div>
          {esLider && miembros.length > 1 && (
            <select style={{ ...s.input, width: 'auto' }} value={profesional} onChange={e => setProfesional(e.target.value)} aria-label="Orientador">
              <option value="">Todo el equipo</option>
              {miembros.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          )}
          <label style={es.check}>
            <input type="checkbox" checked={ocultarCanceladas} onChange={e => setOcultarCanceladas(e.target.checked)} />
            Ocultar canceladas y reprogramadas
          </label>
          <span style={es.resumen}>{programadas} programada{programadas === 1 ? '' : 's'} esta semana</span>
        </div>

        {error && <div style={s.errorBox}>{error}</div>}

        {vistaActual === 'semana' ? (
          <div style={es.semana}>
            {dias.map(({ d, clave, citas: delDia }, i) => (
              <div key={clave} style={{ ...es.dia, ...(clave === hoy ? es.diaHoy : {}) }}>
                <div style={es.diaCabecera}>
                  <span style={es.diaNombre}>{DIAS[i]}</span>
                  <span style={{ ...es.diaNumero, ...(clave === hoy ? es.diaNumeroHoy : {}) }}>{d.getDate()}</span>
                </div>
                <div style={es.diaCitas}>
                  {delDia.map(c => <TarjetaCita key={c.id} cita={c} onClick={() => setAbierta(c)} />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {dias.filter(x => x.citas.length).map(({ d, clave, citas: delDia }) => (
              <div key={clave}>
                <div style={{ ...es.listaDia, ...(clave === hoy ? { color: '#667eea' } : {}) }}>
                  {clave === hoy ? 'Hoy · ' : ''}{d.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {delDia.map(c => <TarjetaCita key={c.id} cita={c} onClick={() => setAbierta(c)} />)}
                </div>
              </div>
            ))}
            {!cargando && !dias.some(x => x.citas.length) && <div style={es.vacio}>No hay citas esta semana.</div>}
          </div>
        )}

        <div style={es.leyenda}>
          {Object.entries(ESTADO_CITA).map(([k, v]) => (
            <span key={k} style={es.leyendaItem}><span style={{ ...es.punto, background: v.color }} />{v.nombre}</span>
          ))}
        </div>
        <div style={es.privacidad}>
          <IconShield size={14} style={{ flexShrink: 0 }} />
          Esta agenda solo la ve el equipo de orientación. No aparece en la Agenda Institucional.
        </div>
      </div>

      {nueva && (
        <CitaModal esLider={esLider} portalFamilia={!!estado?.portal_familia}
          onCerrar={() => setNueva(false)} onGuardada={() => { setNueva(false); cargar(); }} />
      )}
      {abierta && <PanelCita cita={abierta} onCerrar={() => setAbierta(null)} onCambio={cargar} />}
    </div>
  );
}

const es = {
  contenido: { padding: '24px', maxWidth: '1250px', margin: '0 auto', width: '100%' },
  barra: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' },
  navegacion: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  flecha: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', width: '38px', height: '38px', fontSize: '20px', cursor: 'pointer', color: '#555', fontFamily: 'inherit' },
  rango: { fontSize: '15px', fontWeight: 700, color: '#1a1a2e', marginLeft: '6px' },
  filtros: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' },
  selector: { display: 'flex', background: '#fff', borderRadius: '999px', padding: '3px', border: '1px solid #e6e8f0' },
  opcion: { background: 'none', border: 'none', borderRadius: '999px', padding: '6px 14px', fontSize: '13px', fontWeight: 600, color: '#666', cursor: 'pointer', fontFamily: 'inherit' },
  opcionActiva: { background: '#667eea', color: '#fff' },
  check: { display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', color: '#666', cursor: 'pointer' },
  resumen: { fontSize: '13px', color: '#888', marginLeft: 'auto' },
  semana: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px' },
  dia: { background: '#fff', borderRadius: '12px', padding: '8px', minHeight: '220px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 0 },
  diaHoy: { boxShadow: '0 0 0 2px #667eea' },
  diaCabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 2px' },
  diaNombre: { fontSize: '12px', fontWeight: 700, color: '#999', textTransform: 'uppercase' },
  diaNumero: { fontSize: '15px', fontWeight: 800, color: '#1a1a2e', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  diaNumeroHoy: { background: '#667eea', color: '#fff' },
  diaCitas: { display: 'flex', flexDirection: 'column', gap: '6px' },
  listaDia: { fontSize: '13px', fontWeight: 800, color: '#555', textTransform: 'capitalize', marginBottom: '8px' },
  vacio: { background: '#fff', border: '1px dashed #dde0f0', borderRadius: '12px', padding: '24px', textAlign: 'center', color: '#999', fontSize: '14px' },
  leyenda: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '16px', fontSize: '12px', color: '#777' },
  leyendaItem: { display: 'flex', alignItems: 'center', gap: '5px' },
  punto: { width: '10px', height: '10px', borderRadius: '3px' },
  privacidad: { display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', color: '#8a86b3', marginTop: '10px' },
};
